import { useState } from "react";
import { smoothPath, type Pt } from "../lib/curve";
import { Scrub } from "./Scrub";

/**
 * The three link types, each as the sentence the product shows (PRD §7.4, §4.3), with its numbers as handles. The
 * small charts beside them are sketches of what each sentence means, drawn from the numbers in the sentence. They
 * are illustrations, not results.
 */
export function Links() {
	return (
		<section id="links" className="relative mx-auto max-w-[1240px] px-5 pt-24 pb-28 md:px-8 lg:pt-32">
			<div className="grid gap-x-16 gap-y-6 lg:grid-cols-12">
				<h2 className="text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em] lg:col-span-6">Three sentences connect everything.</h2>
				<div className="space-y-4 lg:col-span-5 lg:col-start-8 lg:pt-2">
					<p>
						A forecast on its own is an opinion. Linked to what drives it, it becomes a model. ModelCurve has three kinds of link, and each one reads as a
						sentence you could say in a meeting.
					</p>
					<p className="text-quiet">The numbers in them are handles. Drag one sideways, or focus it and use the arrow keys.</p>
				</div>
			</div>

			<div className="mt-16 border-t border-line">
				<Trigger />
				<Multiplier />
				<Impact />
				<Formula />
			</div>

			<p className="mt-10 max-w-[46rem] text-quiet">
				That is the whole model: events and metrics, a forecast for each, the links between them, and scenarios. The graph, the tables and the comparisons are
				worked out from those, so nothing is kept by hand and nothing goes stale.
			</p>
		</section>
	);
}

function Row({ kind, what, sentence, note, chart }: { kind: string; what: string; sentence: React.ReactNode; note: string; chart?: React.ReactNode }) {
	return (
		<div className="grid gap-x-10 gap-y-5 border-b border-line py-10 lg:grid-cols-12 lg:py-12">
			<div className="lg:col-span-2">
				<h3 className="text-base font-semibold tracking-[-0.01em]">{kind}</h3>
				<p className="text-sm text-quiet">{what}</p>
			</div>
			<div className="lg:col-span-6">
				<p className="voice text-[clamp(1.55rem,2.5vw,2.15rem)] leading-[1.3] text-ink">{sentence}</p>
				<p className="mt-4 max-w-[34rem] text-sm text-quiet">{note}</p>
			</div>
			{chart && <div className="lg:col-span-4">{chart}</div>}
		</div>
	);
}

/* ------------------------------------------------------------------------------------------------ */

const CW = 340;
const CH = 150;
const PL = 4;
const PR = 44;
const PT = 22;
const PB = 22;
const cx = (t: number, span: number) => PL + (t / span) * (CW - PL - PR);
const cy = (v: number, top: number) => PT + (1 - v / top) * (CH - PT - PB);

function Frame({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<svg viewBox={`0 0 ${CW} ${CH}`} className="h-auto w-full overflow-visible" role="img" aria-label={label}>
			<line x1={PL} x2={CW - PR} y1={CH - PB} y2={CH - PB} stroke="var(--ink)" strokeOpacity={0.3} />
			{children}
		</svg>
	);
}

/** "If Acme leaves, 20% chance Initech follows within 3 months." */
function Trigger() {
	const [pct, setPct] = useState(20);
	const [win, setWin] = useState(3);
	const span = 24;
	const t0 = 7;
	const top = 70;
	const base = (t: number) => 0.22 * t;
	const bump = (t: number) => pct * Math.min(1, Math.max(0, (t - t0) / win));
	const ts = Array.from({ length: span + 1 }, (_, t) => t);
	const withIt: Pt[] = ts.map((t) => [cx(t, span), cy(base(t) + bump(t), top)]);
	const without: Pt[] = ts.map((t) => [cx(t, span), cy(base(t), top)]);
	const end = Math.round(base(span) + pct);
	return (
		<Row
			kind="Trigger"
			what="An event makes another more likely, for a while."
			sentence={
				<>
					If Acme leaves, there is a <Scrub label="Chance Initech follows" value={pct} min={5} max={60} step={5} onChange={setPct} format={(v) => `${v}%`} />{" "}
					chance Initech follows within{" "}
					<Scrub label="Window" value={win} min={1} max={12} step={1} onChange={setWin} format={(v) => `${v} month${v === 1 ? "" : "s"}`} />.
				</>
			}
			note="Chains of triggers make cascades. One client leaves, and the one that tends to follow it gets a number."
			chart={
				<Frame label={`Initech's chance of leaving rises by ${pct} points within ${win} months of Acme leaving.`}>
					<line x1={cx(t0, span)} x2={cx(t0, span)} y1={PT - 6} y2={CH - PB} stroke="var(--ink)" strokeOpacity={0.35} strokeDasharray="2 3" />
					<text x={cx(t0, span) + 5} y={PT} fontSize={11} fill="var(--quiet)">
						Acme leaves
					</text>
					<rect x={cx(t0, span)} y={CH - PB + 5} width={cx(t0 + win, span) - cx(t0, span)} height={4} fill="var(--brand-periwinkle)" opacity={0.5} />
					<path d={smoothPath(without)} fill="none" stroke="var(--baseline)" strokeWidth={1.5} strokeDasharray="4 3" />
					<path d={smoothPath(withIt)} fill="none" stroke="var(--brand-periwinkle)" strokeWidth={2.5} />
					<text x={CW - PR + 8} y={cy(base(span) + pct, top) + 4} fontSize={12} fontWeight={600} fill="var(--brand-periwinkle-deeper)">
						{end}%
					</text>
					<text x={CW - PR + 8} y={cy(base(span), top) + 4} fontSize={11} fill="var(--quiet)">
						{Math.round(base(span))}%
					</text>
				</Frame>
			}
		/>
	);
}

/** "While a US recession is under way, an AI crash is 3× as likely." */
function Multiplier() {
	const [k, setK] = useState(3);
	const span = 24;
	const [r0, r1] = [8, 15];
	const s = 1.1;
	const top = 75;
	const cum = (t: number, f: number) => s * (Math.min(t, r0) + f * Math.max(0, Math.min(t, r1) - r0) + Math.max(0, t - r1));
	const ts = Array.from({ length: span * 2 + 1 }, (_, i) => i / 2);
	const line = (f: number): Pt[] => ts.map((t) => [cx(t, span), cy(cum(t, f), top)]);
	const word = k === 1 ? "no more" : k < 1 ? "less" : "more";
	return (
		<Row
			kind="Multiplier"
			what="A condition changes the odds while it holds."
			sentence={
				<>
					While a US recession is under way, an AI crash is{" "}
					<Scrub label="How many times as likely" value={k} min={0} max={5} step={0.5} onChange={setK} format={(v) => `${v}×`} /> as likely.
				</>
			}
			note="A step ladder does the same on a metric: under 25% revenue growth, 3× as likely; between 50% and 100%, normal; above 100%, 0.7×. A 0× step reads “can’t happen”."
			chart={
				<Frame label={`During the recession the crash is ${k} times as likely, so its chance by date climbs ${word} steeply.`}>
					<rect x={cx(r0, span)} y={PT - 6} width={cx(r1, span) - cx(r0, span)} height={CH - PB - PT + 6} fill="var(--baseline)" opacity={0.1} />
					<text x={cx(r0, span) + 5} y={PT + 6} fontSize={11} fill="var(--quiet)">
						Recession
					</text>
					<path d={smoothPath(line(1))} fill="none" stroke="var(--baseline)" strokeWidth={1.5} strokeDasharray="4 3" />
					<path d={smoothPath(line(k))} fill="none" stroke="var(--brand-periwinkle)" strokeWidth={2.5} />
					<text x={CW - PR + 8} y={cy(cum(span, k), top) + 4} fontSize={12} fontWeight={600} fill="var(--brand-periwinkle-deeper)">
						{Math.round(cum(span, k))}%
					</text>
				</Frame>
			}
		/>
	);
}

/** "If Acme leaves, revenue drops 40% over 3 months." */
function Impact() {
	const [drop, setDrop] = useState(30);
	const [ramp, setRamp] = useState(3);
	const span = 24;
	const t0 = 7;
	const top = 10;
	const likely = (t: number) => 8 + 0.02 * t;
	const hit = (t: number) => 1 - (drop / 100) * Math.min(1, Math.max(0, (t - t0) / ramp));
	const ts = Array.from({ length: span * 2 + 1 }, (_, i) => i / 2);
	const mid: Pt[] = ts.map((t) => [cx(t, span), cy(likely(t) * hit(t), top)]);
	const spread = (t: number) => 0.25 + 0.05 * t;
	const hi: Pt[] = ts.map((t) => [cx(t, span), cy((likely(t) + spread(t)) * hit(t), top)]);
	const lo: Pt[] = ts.map((t) => [cx(t, span), cy((likely(t) - spread(t)) * hit(t), top)]);
	const band = `${smoothPath(hi)}L${smoothPath(lo.slice().reverse()).slice(1)}Z`;
	const plain: Pt[] = ts.map((t) => [cx(t, span), cy(likely(t), top)]);
	const endV = likely(span) * hit(span);
	return (
		<Row
			kind="Impact"
			what="An event moves a number."
			sentence={
				<>
					If Acme leaves, revenue drops{" "}
					<Scrub label="How much revenue drops" value={drop} min={5} max={100} step={5} onChange={setDrop} format={(v) => `${v}%`} risk /> over{" "}
					<Scrub label="Over how long" value={ramp} min={1} max={12} step={1} onChange={setRamp} format={(v) => `${v} month${v === 1 ? "" : "s"}`} />.
				</>
			}
			note="The drop can recover over time, too. Metrics carry a Low, Likely and High range, so the effect lands on the whole range, not a single line."
			chart={
				<Frame label={`Revenue falls ${drop}% over ${ramp} months after Acme leaves.`}>
					<line x1={cx(t0, span)} x2={cx(t0, span)} y1={PT - 6} y2={CH - PB} stroke="var(--ink)" strokeOpacity={0.35} strokeDasharray="2 3" />
					<text x={cx(t0, span) + 5} y={PT} fontSize={11} fill="var(--quiet)">
						Acme leaves
					</text>
					<path d={smoothPath(plain)} fill="none" stroke="var(--baseline)" strokeWidth={1.5} strokeDasharray="4 3" />
					<path d={band} fill="var(--brand-periwinkle)" opacity={0.14} />
					<path d={smoothPath(mid)} fill="none" stroke="var(--brand-periwinkle)" strokeWidth={2.5} />
					<text x={CW - PR + 8} y={cy(endV, top) + 4} fontSize={12} fontWeight={600} fill="var(--brand-periwinkle-deeper)">
						${endV.toFixed(1)}M
					</text>
					<text x={CW - PR + 8} y={cy(likely(span), top) + 4} fontSize={11} fill="var(--quiet)">
						${likely(span).toFixed(1)}M
					</text>
				</Frame>
			}
		/>
	);
}

/** Formulas: the only way one metric depends on another (D24). */
function Formula() {
	return (
		<Row
			kind="Formula"
			what="Metrics built from metrics."
			sentence={
				<code className="block font-mono text-[clamp(0.95rem,1.35vw,1.15rem)] leading-relaxed not-italic">
					<span className="text-peri-deeper">headcount_affordable</span> ={" "}
					<span className="text-eggplant">floor((revenue × (1 − target_margin) − fixed_costs) / cost_per_head)</span>
				</code>
			}
			note="Every name in a formula is a driver. It appears on the graph and in the input’s “Used in” list, so the picture can never disagree with the arithmetic."
		/>
	);
}
